import { setupLibrary } from 'lib/fontawesome/fontawesome';
import { isOpenAccess } from 'lib/openAccessUtil';
import groupBy from 'object.groupby';
import LoginPage from 'pages/login/LoginPage';
import { Onboarding } from 'pages/onboarding/Onboarding';
import { OpenAccessLoadingSpinner } from 'pages/openAccess/OpenAccessLoadingSpinner';
import SelectTenant from 'pages/selectTenant/SelectTenant';
import { useTenant } from 'queries/hooks/useTenant';
import { useWorkspaces } from 'queries/hooks/useWorkspaces';
import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Route, Routes } from 'react-router-dom';
import { preloadPageForPath } from 'routes/pageLoaders';
import { RequireOnboarding } from 'routes/RequireOnboarding';
import Auth from 'services/Auth';
import RequireAuth from 'services/Auth/RequireAuth';
import { RequireOpenAccessAuth } from 'services/Auth/RequireOpenAccessAuth';
import ErrorFallback from 'ui/errorHandling/ErrorFallback';
import { AppSkeleton } from 'ui/wrappers/AppSkeleton';
import { SignOutComplete } from './pages/login/SignOutComplete';

groupBy.shim();

/**
 * Only one of the app and open access trees is ever used in a session, so each is loaded on demand.
 */
const loadWrapper = () => import('ui/wrappers/Wrapper');
const Wrapper = lazy(loadWrapper);
const OpenAccessWrapper = lazy(() => import('ui/wrappers/OpenAccessWrapper'));

/**
 * A signed-in user always needs the app shell and the page they opened, so both start downloading as
 * soon as the app starts rather than once React reaches them. Load failures surface when they render.
 */
if (!isOpenAccess() && Auth.user?.tenant) {
    loadWrapper().catch(() => undefined);
    preloadPageForPath(window.location.pathname);
}

/**
 * Starts the tenant and workspace requests the app shell needs while its code is still downloading.
 */
const PrefetchAppData = () => {
    useTenant();
    useWorkspaces();

    return null;
};

export const iconLibrary = setupLibrary();

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false
        }
    }
});

function App() {
    return (
        <div className='w-full h-full overflow-hidden text-textPrimary'>
            <QueryClientProvider client={queryClient}>
                <Toaster
                    containerStyle={{
                        bottom: '4rem',
                        right: '-1.5rem'
                    }}
                />
                {isOpenAccess() && (
                    <Routes>
                        <Route
                            path='/*'
                            element={
                                <RequireOpenAccessAuth>
                                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                                        <Suspense fallback={<OpenAccessLoadingSpinner />}>
                                            <OpenAccessWrapper />
                                        </Suspense>
                                    </ErrorBoundary>
                                </RequireOpenAccessAuth>
                            }
                        />
                    </Routes>
                )}
                {!isOpenAccess() && (
                    <Routes>
                        <Route path='/login/*' element={<LoginPage />} />
                        <Route path='/signoutComplete' element={<SignOutComplete />} />

                        <Route
                            path='/organization'
                            element={
                                <RequireAuth>
                                    <SelectTenant />
                                </RequireAuth>
                            }
                        />

                        <Route
                            path='/onboarding'
                            element={
                                <RequireAuth>
                                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                                        <Onboarding />
                                    </ErrorBoundary>
                                </RequireAuth>
                            }
                        />

                        <Route
                            path='/*'
                            element={
                                <RequireAuth>
                                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                                        <PrefetchAppData />
                                        <RequireOnboarding>
                                            <Suspense fallback={<AppSkeleton />}>
                                                <Wrapper />
                                            </Suspense>
                                        </RequireOnboarding>
                                    </ErrorBoundary>
                                </RequireAuth>
                            }
                        />
                    </Routes>
                )}
                <ReactQueryDevtools initialIsOpen={false} position='bottom-right' />
            </QueryClientProvider>
        </div>
    );
}

export default App;
